// URL como estado do Dashboard do notification-center. Dois modos
// MUTUAMENTE EXCLUSIVOS, ambos compartilháveis pela URL:
//   • Intradiário (default): ?date=yyyy-MM-dd (default hoje) +
//     ?fromHour/?toHour (HH:mm) — série por HORA.
//   • Histórico: ?startDate&?endDate (yyyy-MM-dd) — série por DIA
//     (janela ≤ 92 dias) ou SEMANA (acima), granularidade decidida
//     pelo backend. O modo é INFERIDO da URL: startDate/endDate
//     válidos → histórico; senão → intradiário.
// Padrão espelhado do hook da Consulta
// (use-notification-center-notifications-search-params).
import { useCallback } from "react"
import { useSearchParams } from "react-router-dom"

import type {
  NotificationCenterOverviewParamsType,
  NotificationOverviewIntervalType,
} from "@/types/notification-center"
import { DateHelper } from "@/utils"

// Horas selecionáveis nos selects "De/Até" (buckets de hora cheia).
export const NOTIFICATION_DASHBOARD_HOURS = Array.from(
  { length: 24 },
  (_, hour) => `${String(hour).padStart(2, "0")}:00`
)

// Presets do modo histórico (janela relativa a hoje, em dias). O
// backend decide dia×semana pelo tamanho (≤ 92 dias = dia).
export const NOTIFICATION_DASHBOARD_PRESETS = [
  7, 30, 90,
] as const
export type NotificationDashboardPresetType =
  (typeof NOTIFICATION_DASHBOARD_PRESETS)[number]

export type NotificationDashboardModeType =
  | "intraday"
  | "historical"

// "yyyy-MM-dd" estrito — qualquer outro valor na URL é ignorado.
const DATE_PARAM_PATTERN = /^\d{4}-\d{2}-\d{2}$/

const isValidHour = (
  value: string | null
): value is string =>
  value !== null &&
  NOTIFICATION_DASHBOARD_HOURS.includes(value)

const isValidDate = (
  value: string | null
): value is string =>
  value !== null && DATE_PARAM_PATTERN.test(value)

// Dia default = hoje (regra de produto: intradiário limitado a 1 dia).
export const getNotificationDashboardDefaultDate = () =>
  DateHelper.formatDate(new Date())

// Janela [hoje - (days-1), hoje] em yyyy-MM-dd (preset histórico).
export const resolveNotificationDashboardPresetRange = (
  days: number
): { startDate: string; endDate: string } => {
  const end = new Date()
  const start = new Date()
  start.setDate(start.getDate() - (days - 1))
  return {
    startDate: DateHelper.formatDate(start),
    endDate: DateHelper.formatDate(end),
  }
}

// Resolve o dia selecionado no range da Consulta (que só entende
// startDate/endDate) — deep-links usam startDate = endDate = dia.
export const resolveNotificationDashboardRange = (
  date: string
): { startDate: string; endDate: string } => ({
  startDate: date,
  endDate: date,
})

// Preset ativo (7/30/90) quando a janela histórica bate EXATAMENTE
// com um preset relativo a hoje; senão null (range custom).
const matchHistoricalPreset = (
  startDate: string,
  endDate: string
): NotificationDashboardPresetType | null => {
  const preset = NOTIFICATION_DASHBOARD_PRESETS.find(
    (days) => {
      const range =
        resolveNotificationDashboardPresetRange(days)
      return (
        range.startDate === startDate &&
        range.endDate === endDate
      )
    }
  )
  return preset ?? null
}

const useNotificationCenterDashboardSearchParams = () => {
  const [searchParams, setSearchParams] = useSearchParams()

  // ── Histórico (tem prioridade): startDate/endDate válidos e
  //    start ≤ end e sem futuro (end ≤ hoje). Qualquer falha na
  //    validação cai para o intradiário (nunca janela parcial).
  const rawStartDate = searchParams.get("startDate")
  const rawEndDate = searchParams.get("endDate")
  const today = getNotificationDashboardDefaultDate()
  const isHistorical =
    isValidDate(rawStartDate) &&
    isValidDate(rawEndDate) &&
    rawStartDate <= rawEndDate &&
    rawEndDate <= today
  const mode: NotificationDashboardModeType = isHistorical
    ? "historical"
    : "intraday"
  const startDate = isHistorical
    ? (rawStartDate as string)
    : undefined
  const endDate = isHistorical
    ? (rawEndDate as string)
    : undefined
  const activePreset =
    isHistorical && startDate && endDate
      ? matchHistoricalPreset(startDate, endDate)
      : null

  // ── Intradiário: dia (default hoje) + faixa de horas opcional.
  const rawDate = searchParams.get("date")
  const date = isValidDate(rawDate) ? rawDate : today

  // Faixa de horas: os dois params precisam ser horas válidas E
  // formar um intervalo from < to — senão a faixa toda é ignorada
  // (dia cheio), nunca um recorte parcial/invertido. No histórico as
  // horas são irrelevantes (o backend olha a janela inteira).
  const rawFromHour = searchParams.get("fromHour")
  const rawToHour = searchParams.get("toHour")
  const hasValidHourRange =
    !isHistorical &&
    isValidHour(rawFromHour) &&
    isValidHour(rawToHour) &&
    rawFromHour < rawToHour
  const fromHour = hasValidHourRange
    ? (rawFromHour as string)
    : undefined
  const toHour = hasValidHourRange
    ? (rawToHour as string)
    : undefined

  // Params do overview conforme o modo — NUNCA os dois eixos juntos
  // (o serviço omite undefined; aqui garantimos exclusividade).
  const overviewParams: NotificationCenterOverviewParamsType =
    isHistorical
      ? { startDate, endDate }
      : { date, fromHour, toHour }

  // Range dos deep-links → Consulta (startDate/endDate). No
  // intradiário = dia único; no histórico = a janela.
  const linkRange = isHistorical
    ? { startDate: startDate!, endDate: endDate! }
    : resolveNotificationDashboardRange(date)

  const setDate = useCallback(
    (nextDate: string) => {
      const nextParams = new URLSearchParams(searchParams)
      nextParams.set("date", nextDate)
      setSearchParams(nextParams)
    },
    [searchParams, setSearchParams]
  )

  // Ajusta a faixa de horas preservando a outra ponta atual.
  // Guard from < to: um patch que inverta a faixa é descartado.
  const setHourRange = useCallback(
    (patch: { fromHour?: string; toHour?: string }) => {
      const nextFrom =
        patch.fromHour ??
        fromHour ??
        NOTIFICATION_DASHBOARD_HOURS[0]
      const nextTo =
        patch.toHour ??
        toHour ??
        NOTIFICATION_DASHBOARD_HOURS[
          NOTIFICATION_DASHBOARD_HOURS.length - 1
        ]
      if (nextFrom >= nextTo) return
      const nextParams = new URLSearchParams(searchParams)
      nextParams.set("fromHour", nextFrom)
      nextParams.set("toHour", nextTo)
      setSearchParams(nextParams)
    },
    [fromHour, toHour, searchParams, setSearchParams]
  )

  // Botão "Hoje": volta para o dia atual E limpa a faixa de horas.
  const resetToToday = useCallback(() => {
    const nextParams = new URLSearchParams(searchParams)
    nextParams.set(
      "date",
      getNotificationDashboardDefaultDate()
    )
    nextParams.delete("fromHour")
    nextParams.delete("toHour")
    setSearchParams(nextParams)
  }, [searchParams, setSearchParams])

  // Aplica uma janela histórica (preset OU range custom): grava
  // startDate/endDate e LIMPA os params do intradiário (date/horas)
  // — os dois modos nunca coexistem na URL.
  const setDateRange = useCallback(
    (range: { startDate: string; endDate: string }) => {
      if (range.startDate > range.endDate) return
      const nextParams = new URLSearchParams(searchParams)
      nextParams.set("startDate", range.startDate)
      nextParams.set("endDate", range.endDate)
      nextParams.delete("date")
      nextParams.delete("fromHour")
      nextParams.delete("toHour")
      setSearchParams(nextParams)
    },
    [searchParams, setSearchParams]
  )

  // Preset relativo a hoje (7/30/90 dias).
  const setHistoricalPreset = useCallback(
    (days: number) => {
      setDateRange(
        resolveNotificationDashboardPresetRange(days)
      )
    },
    [setDateRange]
  )

  // Troca de modo pelo toggle. Histórico → default preset de 7 dias;
  // intradiário → limpa a janela e volta pro dia de hoje.
  const switchMode = useCallback(
    (nextMode: NotificationDashboardModeType) => {
      if (nextMode === mode) return
      if (nextMode === "historical") {
        setHistoricalPreset(
          NOTIFICATION_DASHBOARD_PRESETS[0]
        )
        return
      }
      const nextParams = new URLSearchParams(searchParams)
      nextParams.delete("startDate")
      nextParams.delete("endDate")
      nextParams.delete("fromHour")
      nextParams.delete("toHour")
      nextParams.set(
        "date",
        getNotificationDashboardDefaultDate()
      )
      setSearchParams(nextParams)
    },
    [
      mode,
      searchParams,
      setSearchParams,
      setHistoricalPreset,
    ]
  )

  return {
    mode,
    date,
    fromHour,
    toHour,
    startDate,
    endDate,
    activePreset,
    overviewParams,
    linkRange,
    setDate,
    setHourRange,
    resetToToday,
    setDateRange,
    setHistoricalPreset,
    switchMode,
  }
}

// Rótulo amigável do bucket por granularidade (eixo X e legendas):
//   hour → "HH:00" (o próprio bucket)
//   day  → "dd/MM" (a partir de "yyyy-MM-dd")
//   week → "Sxx"   (a partir de "yyyy-Wnn")
export const formatNotificationBucketLabel = (
  bucket: string,
  interval: NotificationOverviewIntervalType
): string => {
  if (interval === "day") {
    const parsed = DateHelper.formatDate(
      new Date(`${bucket}T12:00:00`),
      "dd/MM"
    )
    return parsed || bucket
  }
  if (interval === "week") {
    const match = /^\d{4}-W(\d{2})$/.exec(bucket)
    return match ? `S${match[1]}` : bucket
  }
  return bucket
}

export default useNotificationCenterDashboardSearchParams
